import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { ScreenST } from './screen.st';
import { BreadCrumbST } from './breadcrumb.st';
import { RouterConstant } from '../../../constants/router.constant';
import { TipoEventoConstant } from './../../../constants/tipo-evento.constant';
import { SessionStoreUtil } from 'src/app/utilities/session-store.util';
import { AutenticacionResponseDTO } from 'src/app/dtos/seguridad/autenticacion/autenticacion-response.dto';
import { LabelsConstant } from 'src/app/constants/labels.constant';
import { MenuItemDTO } from 'src/app/dtos/seguridad/menu/menu-item.dto';

/**
 * Se utiliza para administrar el estado del Menu
 */
export class MenuST {

  /** Se utiliza para mostrar/ocultar el menu */
  public isMenuOpen = false;

  /** Indica si el toogle del menu se visualiza por primera vez */
  public isToogleMenuFirstTime = true;

  /** Son los modulos a visualizar en el menu */
  public modulos: Array<MenuItemDTO> | any;

  /** Son los recursos individuales sin padre a visualizar en el menu */
  public recursosIndividuales: Array<MenuItemDTO> | any;

  /** Ítems raíz del menú en el mismo orden que envía el backend (login / persistencia) */
  public menuItemsRaiz: Array<MenuItemDTO> | any;

  /** Contiene la subscripcion del router */
  private subscriptionRouter: Subscription | any;

  /**
   * Cuando se carga la pagina se crea la instancia del estado del menu,
   * se debe tomar los datos del session-store, ya que en este punto son nulos
   *
   * @param screen, se utiliza para validar el tamanio de la pantalla
   * @param bread, se utliza para configurar los datos de la miga de pan
   * @param router, se utiliza para ser notificado cuando el router cambia
   */
  constructor(
    public screen: ScreenST,
    private bread: BreadCrumbST | any,
    private router: Router) {
    this.init();
  }

  /**
   * Metodo que es invocado del constructor de este State
   */
  private init(): void {

    // se obtiene los datos de la autenticacion del session-store
    const auth: AutenticacionResponseDTO = SessionStoreUtil.auth(TipoEventoConstant.GET);

    // se valida que el user si este autenticado
    if (auth && auth.usuario && auth.usuario.idUsuario) {

      // se obtiene los items asociados al usuario del session store
      const items: Array<MenuItemDTO> = SessionStoreUtil.menu(TipoEventoConstant.GET);
      
      if (items && items.length > 0) {
        this.menuItemsRaiz = [...items];
        // se separan los recursos individuales de los módulos con jerarquía
        this.separarRecursosIndividuales(items);
        
        // se obtiene la suscripcion del router para ser notificado
        this.getSuscribeRouter();
      } else {
        this.menuItemsRaiz = [];
      }
    }
  }

  /**
   * Metodo que permite inicializar el Menu, construyendo
   * sus items dependiendo de los privilegios del usuario
   *
   * @param itemsMenu, son los items del menu
   */
  public initMenu(itemsMenu: Array<MenuItemDTO>): void {

    this.menuItemsRaiz = itemsMenu && itemsMenu.length ? [...itemsMenu] : [];

    // se separan los recursos individuales de los módulos con jerarquía
    this.separarRecursosIndividuales(itemsMenu);

    // se obtiene la suscripcion del router para ser notificado
    this.getSuscribeRouter();

    // se configura los items del menu en el localstore (se guarda la data completa)
    SessionStoreUtil.menu(TipoEventoConstant.SET, itemsMenu);
    localStorage.setItem("MENU_PERSIST", JSON.stringify(itemsMenu))
  }

  /**
   * Metodo que separa los recursos individuales (sin padre) de los módulos con jerarquía
   *
   * @param itemsMenu, son todos los items del menu
   */
  private separarRecursosIndividuales(itemsMenu: Array<MenuItemDTO>): void {
    this.modulos = [];
    this.recursosIndividuales = [];

    itemsMenu.forEach(item => {
      // Si el item tiene hijos o no tiene URL, es un módulo con jerarquía
      if ((item.items && item.items.length > 0) || !item.routerLink) {
        this.modulos.push(item);
      } else {
        // Si el item no tiene hijos y tiene URL, es un recurso individual
        this.recursosIndividuales.push(item);
      }
    });
  }

  /**
   * Metodo que permite destruir el Menu liberando memoria,
   * es llamado cuando se cierra la sesion del usuario
   */
  public destroyMenu(): void {
    this.isMenuOpen = false;
    this.isToogleMenuFirstTime = true;
    this.modulos = null;
    this.recursosIndividuales = null;
    this.menuItemsRaiz = null;
    
    // se limpian los datos del session store
    SessionStoreUtil.menu(TipoEventoConstant.ELIMINAR);
    
    if (this.subscriptionRouter) {
      this.subscriptionRouter.unsubscribe();
    }
  }

  /**
   * Metodo que soporta el evento click del boton
   * toogle del menu que se visualiza en el header
   */
  public toggleMenu(): void {
    if (this.isToogleMenuFirstTime && this.screen.isBigScreen()) {
      this.isMenuOpen = true;
    }
    this.isMenuOpen = !this.isMenuOpen;
    this.isToogleMenuFirstTime = false;
  }

  /**
   * Se hace la suscripcion con el router para ser notificado
   * cada vez que el router cambie su navegacion
   */
  private getSuscribeRouter(): void {
    this.subscriptionRouter = this.router.events.subscribe(val => {
      if (val instanceof NavigationEnd) {
        this.notificarItemSeleccionado(val.url);
      }
    });
  }

  /**
   * Metodo que permite notificar el cambio de navegacion
   * al componente miga de pan, para asi pintar la ubicacion
   *
   * @param url, es la nueva url donde el usuario va navegar
   */
  private notificarItemSeleccionado(url: string): void {

    // miga de pan para la pagina de inicio
    if (url.includes(RouterConstant.NAVIGATE_BIENVENIDA)) {
      this.bread.modulo = LabelsConstant.MENU_PAGINA_INICIO;
      this.bread.icono = 'fa-home';
      this.bread.url = null;
      this.bread.urlManualPDF = null;
      this.bread.urlManualVideo = null;
      return;
    }

    // miga de pan para la pagina de configuracion cuenta
    if (url.includes(RouterConstant.ROUTER_CUENTA_USUARIO)) {
      this.bread.modulo = LabelsConstant.MENU_CUENTA_USER;
      this.bread.icono = 'fa-gear';
      this.bread.url = null;
      this.bread.urlManualPDF = null;
      this.bread.urlManualVideo = null;
      return;
    }

    // programacion defensiva para los modulos
    let itemFound: MenuItemDTO | null = null;
    
    if (this.modulos) {

      // se recorre todos los modulos para validar el router de sus items
      for (const modulo of this.modulos) {

        // se recorre los items de este modulo validando su router
        itemFound = this.findItemRecursive(modulo.items as MenuItemDTO[], url);
        if (itemFound) {
          this.bread.icono = modulo.icon;
          this.bread.modulo = modulo.label;
          this.bread.url = ' / ' + this.bread.url;
          this.bread.urlManualPDF = itemFound.urlManualPDF;
          this.bread.urlManualVideo = itemFound.urlManualVideo;
          this.bread.labelSubmodulo = itemFound.label;
          break; // Salir del bucle si se encuentra el item
        }
      }
    }

    // programacion defensiva para los recursos individuales
    if (this.recursosIndividuales && !itemFound) {
      for (const recurso of this.recursosIndividuales) {
        if (recurso.routerLink === url) {
          this.bread.icono = recurso.icon;
          this.bread.modulo = recurso.label;
          this.bread.url = recurso.label;
          this.bread.urlManualPDF = recurso.urlManualPDF;
          this.bread.urlManualVideo = recurso.urlManualVideo;
          this.bread.labelSubmodulo = recurso.label;
          break;
        }
      }
    }
  }

  /**
   * Metodo recursivo para buscar el item seleccionado en el menu
   *
   * @param items, lista de items asociados a un modulo
   * @param url, es la nueva url donde el usuario va navegar
   */
  private findItemRecursive(items: MenuItemDTO[], url: string): MenuItemDTO {
    let itemFound: MenuItemDTO | any = null;

    if(items!= undefined){

      for (const item of items) {
        
        // se verifica si este item tiene mas items
        if (item.items) {
  
          // si hay item seleccionado se configura la miga de pan
          if (itemFound) {
            this.findItemRecursive(item.items as MenuItemDTO[], url);
          } else {
            itemFound = this.findItemRecursive(item.items as MenuItemDTO[], url);
            if (itemFound) {
              this.bread.url = item.label + ' / ' + this.bread.url;
              this.bread.urlManualPDF = itemFound.urlManualPDF;
              this.bread.urlManualVideo = itemFound.urlManualVideo;
              this.bread.labelSubmodulo = itemFound.label;
            }
          }
        }
  
        // se verifica si este item fue seleccionado por el user
        if (url === item.routerLink || url.startsWith(item.routerLink + '/')) {
          itemFound = item;
          this.bread.url = itemFound.label;
        }
      }
    }
    // se recorre todos los items
    return itemFound;
  }
}
